import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { SharedValue } from 'react-native-reanimated';

import { SizedBox } from '@/components/SizedBox';
import { Box } from '@/components/lib/components';

import { CurrencyCard } from './CurrencyCard';
import { useCardPositionStyle, useCardStackAnimation } from './hooks/useCardStackAnimation';
import { CARD_HEIGHT, CARD_WIDTH, CardItem } from './utils';

type WalletCardsProps = {
    cards: CardItem[];
};

const styles = StyleSheet.create({
    cardContainer: {
        position: 'relative',
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        alignSelf: 'center',
        marginBottom: 24,
    },
    card: {
        position: 'absolute',
        width: '100%',
        height: '100%',
    },
});

const AnimatedCardSlot = ({
    position,
    animationProgress,
    cardCount,
    children,
}: {
    position: number;
    animationProgress: SharedValue<number>;
    cardCount: SharedValue<number>;
    children: React.ReactNode;
}) => {
    const animatedStyle = useCardPositionStyle(position, animationProgress, cardCount);
    return <Animated.View style={[styles.card, animatedStyle]}>{children}</Animated.View>;
};

export const WalletCards = ({ cards }: WalletCardsProps) => {
    const currencies = useMemo(() => cards.map(c => c.currency), [cards]);

    const { cardOrder, activeCurrency, animationProgress, cardCount, handleCardPress } =
        useCardStackAnimation(currencies);

    const cardsByCurrency = useMemo(() => {
        const lookup: Record<string, CardItem> = {};
        cards.forEach(card => {
            lookup[card.currency] = card;
        });
        return lookup;
    }, [cards]);

    return (
        <Box>
            <SizedBox value={38 + Math.max(0, cardOrder.length - 2) * 50} />
            <Box style={styles.cardContainer}>
                {cardOrder.map((currency, position) => {
                    const card = cardsByCurrency[currency];
                    if (!card) return null;

                    const isActive = currency === activeCurrency;

                    return (
                        <AnimatedCardSlot
                            key={currency}
                            position={position}
                            animationProgress={animationProgress}
                            cardCount={cardCount}
                        >
                            <CurrencyCard
                                title={card.info.title}
                                touchpointText={isActive ? card.info.touchpointText : card.amount}
                                amount={card.amount}
                                description={card.info.description}
                                backgroundColor={card.style.cardColor}
                                textColor={card.style.textColor}
                                icon={card.style.icon}
                                onPress={() => handleCardPress(currency)}
                                isActive={isActive}
                                currency={currency}
                            />
                        </AnimatedCardSlot>
                    );
                })}
            </Box>
        </Box>
    );
};
